
interface ComboPricingProps {
  lang: 'en' | 'ja';
}

export default function ComboPricing({ lang }: ComboPricingProps) {
  const handleWhatsApp = () => {
    const msg = lang === 'en'
      ? "Hi! I'd like to book the Tokyo Night Combo Experience. Could you share availability?"
      : "こんにちは！Tokyo Night Combo Experienceの予約をしたいです。空き状況を教えてください。";
    window.open(`https://wa.me/819092895522?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const t = {
    en: {
      badge: 'Pricing',
      title: 'Simple & Transparent Pricing',
      plans: [
        {
          label: 'Base Package',
          guests: '1–2 people',
          price: '¥30,000',
          highlight: true,
          features: [
            'Local izakaya experience (~2 hrs)',
            'Karaoke box experience (~2 hrs)',
            'Friendly local guide throughout',
            'Venue booking & check-in handled',
            'Song recommendations & support',
            'Total ~4–5 hours',
          ],
        },
        {
          label: 'Additional Guest',
          guests: 'per person',
          price: '+¥5,000',
          highlight: false,
          features: [
            'Up to 4 people total',
            'Same full experience',
            'Private group setting',
          ],
        },
      ],
      notice: [
        'Food, drinks & room fees paid directly at each venue',
        'Alcohol served by each venue (not by our service)',
        'This service is not a transportation service',
        'Max 4 guests per session',
      ],
      cta: 'Book via WhatsApp',
      sub: 'No credit card needed · WhatsApp booking only',
    },
    ja: {
      badge: '料金',
      title: 'シンプルで明確な料金体系',
      plans: [
        {
          label: 'ベースパッケージ',
          guests: '1〜2名',
          price: '¥30,000',
          highlight: true,
          features: [
            '地元居酒屋体験（約2時間）',
            'カラオケボックス体験（約2時間）',
            '終始同行する地元ガイド',
            '会場予約・受付対応込み',
            '曲のレコメンド・操作サポート',
            '合計約4〜5時間',
          ],
        },
        {
          label: '追加ゲスト',
          guests: '1名あたり',
          price: '+¥5,000',
          highlight: false,
          features: [
            '最大4名まで',
            '同じ体験内容',
            'プライベートグループ',
          ],
        },
      ],
      notice: [
        '飲食代および部屋代は各店舗へ直接お支払いください',
        '酒類の提供は各店舗が行います（当サービスは提供しません）',
        '当サービスは運送業ではありません',
        '1セッション最大4名まで',
      ],
      cta: 'WhatsAppで予約する',
      sub: 'クレジットカード不要 · WhatsApp予約のみ',
    },
  }[lang];

  return (
    <section className="py-20 px-4 bg-gray-950 text-white">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full mb-5">
            <i className="ri-price-tag-3-line text-amber-400 text-sm"></i>
            <span className="text-gray-300 text-sm">{t.badge}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white">{t.title}</h2>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {t.plans.map((plan, i) => (
            <div
              key={i}
              className={`rounded-2xl p-7 border transition-all duration-300 ${
                plan.highlight
                  ? 'bg-gradient-to-br from-amber-900/40 to-pink-900/30 border-amber-500/50 shadow-xl shadow-amber-900/20'
                  : 'bg-white/5 border-white/10'
              }`}
            >
              {plan.highlight && (
                <div className="inline-block text-xs font-bold bg-gradient-to-r from-amber-500 to-pink-500 text-white px-3 py-1 rounded-full mb-4">
                  Most Popular
                </div>
              )}
              <div className="text-gray-400 text-sm mb-1">{plan.label}</div>
              <div className="text-gray-500 text-xs mb-3">{plan.guests}</div>
              <div className="text-4xl font-extrabold text-white mb-6">{plan.price}</div>
              <ul className="space-y-2.5">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-sm text-gray-300">
                    <i className="ri-check-line text-amber-400 mt-0.5 flex-shrink-0"></i>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Notice */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-10">
          <div className="flex items-center gap-2 mb-4">
            <i className="ri-information-line text-amber-400 text-lg"></i>
            <span className="text-white font-semibold text-sm">
              {lang === 'en' ? 'Important Notes' : '重要事項'}
            </span>
          </div>
          <ul className="space-y-2">
            {t.notice.map((n, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                <i className="ri-error-warning-line text-amber-500/70 mt-0.5 flex-shrink-0"></i>
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={handleWhatsApp}
            className="inline-flex items-center gap-3 px-10 py-4 bg-green-500 hover:bg-green-600 text-white text-lg font-bold rounded-full shadow-2xl hover:shadow-green-500/30 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer mb-3"
          >
            <i className="ri-whatsapp-line text-2xl"></i>
            {t.cta}
          </button>
          <p className="text-gray-500 text-xs">{t.sub}</p>
        </div>
      </div>
    </section>
  );
}
